import React from "react";

import styled from "styled-components";

import { Card, CardBody, Col, Container, InputGroup, Row } from "reactstrap";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  getSelectedMonthAgoWithFirstDay,
  getCurrentDay,
  numberWithCommas,
  getSelectedMonthAgoWithLastDay,
  downloadCSVReactTable,
} from "../../../../helpers/utils";
import LineChartManager from "../common/LineChartManager";
import CustomSelect from "../../../../components/Common/CustomSelect";
import HorizontalBarChart from "../common/barComponents/HorizontalBarChart";
import DynamicFormFilters from "../../../../components/forms/DynamicFormFilters";
import AggregatedTable from "../common/tables/AggregatedTable";
import LineChartOffers from "../common/LineChartOffers";

const DEFAULT_REPORT_WIDGET = [
  {
    icon: "bx bx bx-table",
    title: "Plan",
    value: 0,
  },
  {
    icon: "bx bx bx-stats",
    title: "Profit",
    value: 0,
  },
  {
    icon: "bx bx-wallet-alt",
    title: "Rent profit",
    value: 0,
  },
  {
    icon: "mdi mdi-cash-plus",
    title: "Percent %",
    value: 0,
  },
  {
    icon: "bx bx-credit-card",
    title: "Debit",
    value: 0,
  },
  {
    icon: "bx bxs-credit-card",
    title: "Overdue debt",
    value: 0,
  },
  {
    icon: "bx bx-money",
    title: "Costs",
    value: 0,
  },
];

const spinnerWidget = (
  <div className="loader-for-react-widget">
    <div className="spinner-border text-primary m-1" role="status">
      <span className="sr-only">Loading...</span>
    </div>
  </div>
);

const execAddonsChart = {
  tooltip: {
    enabled: true,
    y: {
      formatter: value => `${numberWithCommas(parseFloat(value))}%`,
      title: {
        formatter: tooltipName => "",
      },
    },
  },
};

const marginAddonsChart = {
  tooltip: {
    enabled: true,
    y: {
      formatter: value => `${numberWithCommas(parseFloat(value))}%`,
      title: {
        formatter: tooltipName => "",
      },
    },
  },
  yaxis: {
    labels: {
      show: true,
      formatter: value => `${value}%`,
    },
  },
};

const indexOptions = [
  {
    id: "profit",
    name: "Profit",
  },
  {
    id: "cost",
    name: "Cost",
  },
  {
    id: "margin",
    name: "Margin",
  },
];

const DefaultOverview = props => {
  const [date, setDate] = React.useState({
    start_date: getSelectedMonthAgoWithFirstDay(0),
    end_date: getSelectedMonthAgoWithLastDay(-1),
  });
  const [marginData, setMarginData] = React.useState({});
  const [execData, setExecData] = React.useState({});
  const [activeFilters, setActiveFilters] = React.useState({});
  const [tablesDataForExport, setTablesDataForExport] = React.useState({});
  const [indexSelected, setIndexSelected] = React.useState(indexOptions[0]);
  const [isAdmin, setIsAdmin] = React.useState({
    id: "",
    status: false,
  });

  const labelsByDay = {
    profit: `${props.permissions["is_admin"] || props.permissions["is_hr"]
      ? 'Total Project'
      : 'Total Manager'
      } profit`,
    cost: `${props.permissions["is_admin"] || props.permissions["is_hr"]
      ? 'Total Project'
      : 'Total Manager'
      } cost`,
    margin: `${props.permissions["is_admin"] || props.permissions["is_hr"]
      ? 'Total Project'
      : 'Total Manager'
      } margin`,
  };

  const labelsByDayOffers = {
    profit: "Total offers profit by day",
    cost: "Total offers cost by day",
    margin: "Total offers margin by day",
  };

  const getPermsIsMediaBuyer = () => {
    const { permissions } = props;

    if (permissions["is_admin"] || permissions['is_finance'] || permissions['is_hr']) {
      return false;
    } else if (permissions["is_head_of_mediabuyer"] || permissions["is_mediabuyer"]) {
      return true;
    }
    return false;
  }

  const renderOverviewName = () => {
    const { permissions } = props;
    if (
      permissions["is_head_of_mediabuyer"] ||
      permissions["is_head_of_accountmanager"] ||
      permissions["is_admin"] ||
      permissions['is_finance'] ||
      permissions['is_hr']
    ) {
      return "Top Management";
    }

    if (permissions["is_accountmanager"] || permissions["is_mediabuyer"]) {
      return "Individual Report";
    }

    return "Top Management";
  };

  const handleUserPosition = (user, managers) => {
    const { permissions } = props;

    if (
      permissions["is_head_of_mediabuyer"] ||
      permissions["is_head_of_accountmanager"] ||
      permissions["is_admin"] ||
      permissions['is_finance'] ||
      permissions['is_hr']
    ) {
      return setIsAdmin({ status: true, id: "" });
    }

    if (permissions["is_accountmanager"]) {
      let idx = managers.findIndex(m => m.email == user.email);
      idx > 0 && setIsAdmin({ status: false, id: managers[idx].id });
    }

    if (permissions["is_mediabuyer"]) {
      let idx = managers.findIndex(m => m.email == user.email);
      idx > 0 && setIsAdmin({ status: false, id: managers[idx].id });
    }
  };

  React.useEffect(() => {
    const { permissions } = props;

    let data = {};
    data.start_date = getSelectedMonthAgoWithFirstDay(0);
    data.end_date = getSelectedMonthAgoWithLastDay(-1);

    if (
      permissions["is_head_of_mediabuyer"] ||
      permissions["is_head_of_accountmanager"] ||
      permissions["is_admin"] ||
      permissions['is_finance'] ||
      permissions['is_hr']
    ) {
      props.getCustomFilters({ buyer: false });
      props.getTopManagementReportsFilters({
        date_from: data.start_date,
        date_to: data.end_date,
      });
    } else {
      if (permissions["is_accountmanager"]) {
        props.getCustomFilters({ buyer: false });
        props.getTopManagementReportsFilters({
          // account_manager: props.user.id,
          date_from: data.start_date,
          date_to: data.end_date,
        });
      } else if (permissions["is_mediabuyer"]) {
        props.getCustomFilters({ buyer: true });
        props.getTopManagementReportsFilters({
          media_buyer: props.user.id,
          date_from: data.start_date,
          date_to: data.end_date,
        });
      }
    }

    setIndexSelected(indexOptions[0]);

    return () => {
      setIndexSelected({});
      setExecData({});
      setMarginData({});
      props.setDataByDays([]);
      props.setProfitPlansWidgetData(DEFAULT_REPORT_WIDGET);
      props.setTopManagementReportsFilters({
        offers: [],
        managers: [],
        buyers: [],
        platforms: [],
      });
      props.setCustomProfitByDays([]);
    };
  }, []);

  React.useEffect(() => {
    if (props.topReportsFilters?.managers?.length > 0) {
      handleUserPosition(props.user, props.topReportsFilters.managers_more);
    }
  }, [props.topReportsFilters]);

  React.useEffect(() => {
    if (props.filter_data?.all_account_managers) {
      let dt = {
        date_from: getSelectedMonthAgoWithFirstDay(3),
        date_to: getCurrentDay(),
        account_manager: props.filter_data.all_account_managers[0]?.value,
      };
      props.getDataByDays({
        ...dt,
        ...(getPermsIsMediaBuyer() && { group_by: 'offers' })
      });
      props.getTotalPlan({
        ...dt,
      });
    }
  }, [props.filter_data]);

  const getUserPermFiltersData = (filterDate) => {
    const { permissions } = props;
    if (
      permissions["is_head_of_mediabuyer"] ||
      permissions["is_head_of_accountmanager"] ||
      permissions["is_admin"] ||
      permissions['is_finance'] ||
      permissions['is_hr']
    ) {
      return {
        date_from: filterDate.start_date,
        date_to: filterDate.end_date,
      }
    } else {
      if (permissions["is_accountmanager"]) {
        return {
          // account_manager: props.user.id,
          date_from: filterDate.start_date,
          date_to: filterDate.end_date,
        }
      } else if (permissions["is_mediabuyer"]) {
        return {
          media_buyer: props.user.id,
          date_from: filterDate.start_date,
          date_to: filterDate.end_date,
        }
      }
    }
  }

  const handleDateRangeChange = async (filterDate) => {
    const { execFilters, marginFilters } = props.customFilters;

    let paramsForFilters = getUserPermFiltersData(filterDate)

    setDate(filterDate);

    props.getMarginPlan({
      group: marginData.value || marginFilters[0].value,
      date_from: filterDate.start_date,
      date_to: filterDate.end_date,
    });

    props.getExecPlan({
      group: execData.value || execFilters[0].value,
      date_from: filterDate.start_date,
      date_to: filterDate.end_date,
    });

    props.getDataByDays(
      {
        ...activeFilters,
        date_from: filterDate.start_date,
        date_to: filterDate.end_date,
        ...(getPermsIsMediaBuyer() && { group_by: 'offers' })
      },
      indexSelected.id,
      {
        ...paramsForFilters,
      }
    );

    props.getTotalPlan({
      ...activeFilters,
      date_from: filterDate.start_date,
      date_to: filterDate.end_date,
    });

  };

  const activeAccountManager = managers => {
    if (
      isAdmin?.status ||
      props.permissions["is_head_of_mediabuyer"] ||
      props.permissions["is_head_of_accountmanager"] ||
      props.permissions["is_mediabuyer"]
    ) {
      return managers;
    } else {
      return props.user.id;
    }
  };

  const activeBuyers = buyers => {
    if (
      isAdmin?.status ||
      props.permissions["is_admin"] ||
      props.permissions["is_head_of_mediabuyer"] ||
      props.permissions["is_head_of_accountmanager"] ||
      props.permissions['is_finance'] ||
      props.permissions['is_hr']
    ) {
      return buyers;
    } else {
      return props.user.id;
    }
  };

  const showAggreagatedManagersTable = () => {
    if (
      props.permissions["is_admin"] ||
      props.permissions["is_head_of_mediabuyer"] ||
      props.permissions["is_head_of_accountmanager"] ||
      props.permissions["is_accountmanager"] ||
      props.permissions['is_finance'] ||
      props.permissions['is_hr']
    ) {
      return true;
    }

    if (props.permissions["is_mediabuyer"]) {
      return false;
    }
  };
  const handleMainFiltersTrigger = (filter, type, status) => {
    const { execFilters, marginFilters } = props.customFilters;
    const { permissions } = props;
    let primaryChanged = "";
    let selectedIdx = "";
    let params = {};
    let days = {
      date_from: filter.start_date || date.start_date,
      date_to: filter.end_date || date.end_date,
    };

    if (filter?.managers && filter?.managers.length >= 0) {
      if (
        // permissions["is_head_of_mediabuyer"] ||
        // permissions["is_head_of_accountmanager"] ||
        permissions["is_admin"] ||
        permissions['is_finance'] ||
        permissions['is_hr']
      ) {
        params.account_manager = filter.managers.join(",");
      } else if (permissions["is_accountmanager"] || permissions["is_head_of_accountmanager"]) {
        // params.account_manager = props.user.id;
        params.account_manager = filter.managers.join(",");
      } else {
        params.account_manager = filter.managers.join(",");
      }
    }
    if (filter?.buyers && filter?.buyers.length >= 0) {
      if (
        // permissions["is_head_of_mediabuyer"] ||
        // permissions["is_head_of_accountmanager"] ||
        permissions["is_admin"] ||
        permissions['is_finance'] ||
        permissions['is_hr']
      ) {
        params.buyers = filter.buyers.join(",");
      } else if (permissions["is_mediabuyer"] || permissions["is_head_of_mediabuyer"]) {
        params.buyers = props.user.id;
      } else {
        params.buyers = filter.buyers.join(",");
      }
    }
    if (filter?.offers && filter?.offers.length > 0) {
      params.offer = filter.offers.join(",");
    }

    if (filter?.platforms && filter?.platforms.length > 0)
      params.platforms = filter.platforms.join(",");

    if (filter?.index) {
      let idx = indexOptions.findIndex(i => i.id === filter.index.join(","));
      setIndexSelected(indexOptions[idx]);
      selectedIdx = filter.index.join(",");
    } else {
      selectedIdx = indexSelected.id;

      setActiveFilters({
        ...params,
        ...days,
        account_manager: activeAccountManager(params.account_manager),
        buyers: activeBuyers(params.buyers),
      });
    }

    Object.keys(params).forEach(key => {
      if (key in activeFilters) {
        if (params[key]?.length !== activeFilters[key]?.length) {
          primaryChanged = key;
        }
      }
    });

    setDate({
      start_date: filter.start_date || date.start_date,
      end_date: filter.end_date || date.end_date,
    });

    if (params?.buyers == "") {
      delete params.buyers;
    }

    if (params?.account_manager == "") {
      delete params.account_manager;
    }

    if (type != "permit") {
      if (
        status == "INITIAL" ||
        primaryChanged == "account_manager" ||
        primaryChanged == "buyers" ||
        primaryChanged == "offer"
      ) {
        props.getDataByDays(
          {
            ...days,
            ...params,
            ...(getPermsIsMediaBuyer() && { group_by: 'offers' })
          },
          selectedIdx
        );
        props.getTotalPlan({
          ...days,
          ...params,
        });
      }

      props.getProfitPlanOffers(
        {
          ...days,
          ...params,
        },
        (!permissions["is_admin"] && !permissions['is_hr'] && permissions["is_mediabuyer"]) || false
      );

      props.getExecPlan({
        group: execData?.value || execFilters[0].value,
        ...days,
        ...params,
      });

      props.getMarginPlan({
        group: marginData?.value || marginFilters[0].value,
        ...days,
        ...params,
      });

      props.getProfitPlansWidgetData({
        ...params,
        ...days,
      });

      props.getAggregatedManagersProfit({
        ...params,
        ...days,
      });

      props.getAggregatedOfferProfit({
        ...params,
        ...days,
      });
    } else {
      props.getDataByDays(
        {
          ...days,
          ...params,
          ...(activeAccountManager(activeFilters.account_manager) && { account_manager: activeAccountManager(activeFilters.account_manager) }),
          // account_manager: activeAccountManager(activeFilters.account_manager),
          // buyres: activeBuyers(activeFilters.buyers),
          ...(getPermsIsMediaBuyer() && { group_by: 'offers' })
        },
        selectedIdx
      );
      props.getTotalPlan({
        ...days,
        ...params,
        account_manager: activeAccountManager(activeFilters.account_manager),
        buyres: activeBuyers(activeFilters.buyers),
      });
    }

    setTablesDataForExport({});
  };

  const handleChangeMarginFilter = val => {
    const { marginFilters } = props.customFilters;
    setMarginData(val);
    props.getMarginPlan({
      group: val.value || marginFilters[0].value,
      date_from: date.start_date,
      date_to: date.end_date,
      ...activeFilters,
    });
  };

  const handleChangeExecFilter = val => {
    const { execFilters } = props.customFilters;
    setExecData(val);
    props.getExecPlan({
      group: val.value || execFilters[0].value,
      date_from: date.start_date,
      date_to: date.end_date,
      ...activeFilters,
    });
  };

  const handleChangeTableData = type => {
    const { start_date, end_date } = date;
    downloadCSVReactTable(
      tablesDataForExport[type]?.data,
      [],
      `Aggregated-${type.charAt(0).toUpperCase() + type.slice(1)
      }Profit[${start_date} - ${end_date}]`
    );
  };

  const handleUpdateTable = data => {
    setTablesDataForExport({
      ...tablesDataForExport,
      ...data,
    });
  };

  const handleShowAccountManagers = () => {
    if (
      props.permissions["is_admin"] ||
      props.permissions["is_head_of_accountmanager"] ||
      props.permissions['is_finance'] ||
      props.permissions['is_hr']
    )
      return false;
    if (
      props.permissions["is_accountmanager"] ||
      props.permissions?.["is_head_of_mediabuyer"] ||
      props.permissions?.["is_mediabuyer"]
    )
      return true;
  };

  const handleShowBuyers = () => {
    if (
      props.permissions["is_admin"] ||
      props.permissions["is_head_of_mediabuyer"] ||
      props.permissions['is_finance'] ||
      props.permissions['is_hr']
    )
      return false;
    if (
      props.permissions["is_mediabuyer"] ||
      props.permissions["is_accountmanager"]
    )
      return true;
  };

  const filteringWidgetRow = () => {
    if (props.permissions["is_admin"] || props.permissions['is_finance'] || props.permissions['is_hr']) {
      return 3;
    } else if (props.permissions?.["is_head_of_mediabuyer"]) {
      return 3;
    } else if (
      props.permissions?.["is_mediabuyer"] ||
      props.permissions?.["is_accountmanager"]
    ) {
      return 3;
    } else {
      return 3;
    }
  };

  const handleAggregatedTableChange = (table_data) => {
    const params = {
      date_from: activeFilters.date_from,
      date_to: activeFilters.date_to,
      offer: activeFilters.offer,
      ordering: table_data.ordering
    }
    props.getAggregatedOfferProfit(params);
  }

  const handleAggregatedManagersTableChange = (table_data) => {
    const params = {
      date_from: activeFilters.date_from,
      date_to: activeFilters.date_to,
      offer: activeFilters.offer,
      ordering: table_data.ordering
    }
    props.getAggregatedManagersProfit(params);
  }

  return (
    <>
      <React.Fragment>

        {Object.entries(props.customFilters).length > 0 ? (
          <StyledContainer fluid>
            <Breadcrumbs
              title="Dashboard"
              breadcrumbItem={renderOverviewName()}
              link="/overview"
            />
            <DynamicFormFilters
              onFiltersChange={handleMainFiltersTrigger}
              onDateRangeChange={handleDateRangeChange}
              isLoadingSelect={props.topReportsFiltersLoading}
              disableFilterBy
              filtersFields={[
                {
                  filterData: props.topReportsFilters?.offers,
                  title: "Offers",
                  plug: "Offers...",
                  filterType: "offers",
                  customWidth: "230px",
                },
                {
                  filterData: props.topReportsFilters?.platforms,
                  title: "Platforms",
                  plug: "Project Platforms...",
                  filterType: "platforms",
                  customWidth: "230px",
                },
                {
                  filterData: props.topReportsFilters?.managers,
                  title: "Project Managers",
                  plug: "Managers...",
                  filterType: "managers",
                  customWidth: "230px",
                  hidden: handleShowAccountManagers(),
                },
                {
                  filterData: props.topReportsFilters?.buyers,
                  title: "Buyers",
                  plug: "Buyers...",
                  filterType: "buyers",
                  customWidth: "230px",
                  hidden: handleShowBuyers(),
                },
                {
                  filterData: indexOptions,
                  title: "Index",
                  plug: "Index...",
                  filterType: "index",
                  customWidth: "230px",
                  defaultValue: indexSelected || indexOptions[0],
                  singleSelect: true,
                },
              ]}
              filtered_by={[
                "offers",
                "platforms",
                "managers",
                "buyers",
                "index",
              ]}
              filtered_params={['buyers', 'managers', 'platforms']}
              selectedDate={[]}
              dateFilters={[
                {
                  plug: "Period",
                  key: "period",
                  filterType: ["date_from", "date_to"],
                  value: date,
                  clearDate: false,
                  customWidth: "230px",
                },
              ]}
            />

            <Row>
              {props.topReportsWidgets
                .filter((_, index) => {
                  if (
                    !props.permissions["is_admin"] && !props.permissions['is_finance'] && !props.permissions['is_hr'] &&
                    props.permissions?.["is_accountmanager"]
                  ) {
                    return index !== 3 && index !== 4 && index !== 2;
                  }

                  if (
                    !props.permissions["is_admin"] && !props.permissions['is_hr'] && !props.permissions['is_finance'] &&
                    props.permissions?.["is_mediabuyer"]
                  ) {
                    return index !== 3 && index !== 4 && index !== 2;
                  }

                  return _;
                })
                .map((i, k) => (
                  <Col
                    xl={filteringWidgetRow()}
                    md={6}
                    sm={6}
                    key={"_col_" + k}
                  >
                    <StyledCard className="mini-stats-wid fade-in-custom-animate-1s">
                      <CardBody>
                        {props.topReportsLoading && spinnerWidget}
                        <div className="d-flex" style={{ height: "65px" }}>
                          <div className="flex-grow-1">
                            <p className="text-muted fw-medium fade-in-custom-animate">
                              {i.title}
                            </p>
                            {/* {!props.topReportsLoading && ( */}
                            <h4 className="mb-0 fade-in-custom-animate">
                              {`${numberWithCommas(i.value)}${k == 3 ? "%" : "$"
                                }`}
                            </h4>
                            {/* )} */}
                          </div>
                          <div className="d-flex justify-content-end align-items-center">
                            <div className="avatar-xs">
                              <span className="avatar-title rounded-circle bg-primary bg-soft text-primary font-size-18 fade-in-custom-animate">
                                <i className={i.icon} />
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </StyledCard>
                  </Col>
                ))}
            </Row>

            <Row>
              <Col sm={12}>
                <Card>
                  <CardBody>
                    <Row className="justify-content-between align-middle mb-3">
                      <Col
                        sm={6}
                        className="d-flex justify-content-start align-items-center"
                      >
                        <h5 className="m-0">
                          {getPermsIsMediaBuyer() ? labelsByDayOffers[indexSelected.id] : labelsByDay[indexSelected.id]}
                        </h5>
                      </Col>
                      <Col
                        sm={6}
                        className="d-flex justify-content-end align-items-center"
                      >
                        <div className="d-flex"></div>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={12}>
                        {props.loaderDataByDays && (
                          <div className="loader-for--line-chart justify-content-center align-items-center">
                            <div
                              className="spinner-border text-primary m-1"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          </div>
                        )}
                        {getPermsIsMediaBuyer() ? (
                          <LineChartOffers
                            data={props.dataByDays}
                            plan={props.totalPlan}
                            type={indexSelected.id}
                            type_name={indexSelected.name}
                          />
                        ) : (
                          <LineChartManager
                            data={props.dataByDays}
                            plan={props.totalPlan}
                            type={indexSelected.id}
                            type_name={indexSelected.name}
                          />
                        )}

                        {/* // )} */}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col xl={6}>
                <Card>
                  <CardBody>
                    <Row className="justify-content-between align-middle mb-3">
                      <Col
                        sm={6}
                        className="d-flex justify-content-start align-items-center"
                      >
                        <h5 className="m-0">Margin chart</h5>
                      </Col>
                      <Col
                        sm={6}
                        className="d-flex justify-content-end align-items-center"
                      >
                        <div className="d-flex">
                          <StyledManager>
                            <CustomSelect
                              isLoading={props.customLoader}
                              defaultValue={
                                props.customFilters?.marginFilters
                                  ? props.customFilters.marginFilters[0]
                                  : ""
                              }
                              value={marginData}
                              onChange={handleChangeMarginFilter}
                              options={
                                props.customFilters.marginFilters || []
                              }
                              classNamePrefix="select2-selection"
                              required
                            />
                          </StyledManager>
                        </div>
                      </Col>
                    </Row>
                    <Row className="justify-content-between"></Row>
                    <HorizontalBarChart
                      data={props.profitPlanMargin}
                      addons={marginAddonsChart}
                    />
                  </CardBody>
                </Card>
              </Col>
              {/* <Col xl={4}>
                    <Card>
                      <CardBody>
                        <Row className="justify-content-between align-middle mb-3">
                          <Col
                            sm={6}
                            className="d-flex justify-content-start align-items-center"
                          >
                            <h5 className="m-0">Offers chart</h5>
                          </Col>
                          <Col
                            sm={6}
                            className="d-flex justify-content-end align-items-center h-[15px]"
                          >
                            <div style={{ height: 38 }} />
                          </Col>
                        </Row>
                        <Row className="justify-content-between"></Row>
                        <HorizontalBarChart
                          data={props.profitPlanOffers}
                          addons={offersAddonsChart}
                        />
                      </CardBody>
                    </Card>
                  </Col> */}
              <Col xl={6}>
                <Card>
                  <CardBody>
                    <Row className="justify-content-between align-middle mb-3">
                      <Col
                        sm={6}
                        className="d-flex justify-content-start align-items-center"
                      >
                        <h5 className="m-0">Plans Execution</h5>
                      </Col>
                      <Col
                        sm={6}
                        className="d-flex justify-content-end align-items-center"
                      >
                        <div className="d-flex">
                          <StyledManager>
                            <CustomSelect
                              isLoading={props.customLoader}
                              defaultValue={
                                props.customFilters?.execFilters
                                  ? props.customFilters.execFilters[0]
                                  : ""
                              }
                              value={execData}
                              onChange={handleChangeExecFilter}
                              options={props.customFilters?.execFilters || []}
                              classNamePrefix="select2-selection"
                              required
                            />
                          </StyledManager>
                        </div>
                      </Col>
                    </Row>
                    <Row className="justify-content-between"></Row>
                    <HorizontalBarChart
                      data={props.profitPlanExecution}
                      addons={execAddonsChart}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              {showAggreagatedManagersTable() && (
                <Col xl={12}>
                  <Card>
                    <CardBody id="pdf-exporter--managers">
                      <Row
                        className="justify-content-between align-middle mb-3"
                        data-html2canvas-ignore="true"
                      >
                        <Col
                          sm={6}
                          className="d-flex justify-content-start align-items-center"
                        >
                          <h5 className="m-0">Aggregated Project Managers profit</h5>
                        </Col>
                        <Col
                          sm={6}
                          className="d-flex justify-content-end align-items-center h-[15px]"
                        >
                          <div className="float-end" style={{ height: 38 }}>
                            <Link
                              to="#"
                              onClick={() =>
                                handleChangeTableData("managers")
                              }
                              className={`btn btn-success ${!tablesDataForExport?.managers && "disabled"
                                } me-2`}
                            >
                              <i className="fas fa-file-csv" />
                            </Link>
                          </div>
                        </Col>
                      </Row>
                      <Row className="justify-content-between"></Row>
                      <AggregatedTable
                        tableName="managers"
                        data={props.aggregatedManagersData}
                        handleTableChange={handleAggregatedManagersTableChange}
                        loader={props.loaderAggregatedManagers}
                        setTablesDataForExport={handleUpdateTable}
                      />
                    </CardBody>
                  </Card>
                </Col>
              )}

              <Col xl={12}>
                <Card>
                  <CardBody id="pdf-exporter--offers">
                    <Row
                      className="justify-content-between align-middle mb-3"
                      data-html2canvas-ignore="true"
                    >
                      <Col
                        sm={6}
                        className="d-flex justify-content-start align-items-center"
                      >
                        <h5 className="m-0">Aggregated offers profit</h5>
                      </Col>
                      <Col
                        sm={6}
                        className="d-flex justify-content-end align-items-center h-[15px]"
                      >
                        <div className="float-end" style={{ height: 38 }}>
                          <Link
                            to="#"
                            onClick={() => handleChangeTableData("offers")}
                            className={`btn btn-success ${!tablesDataForExport?.offers && "disabled"
                              } me-2`}
                          >
                            <i className="fas fa-file-csv" />
                          </Link>
                        </div>
                      </Col>
                    </Row>
                    <Row className="justify-content-between"></Row>
                    <AggregatedTable
                      tableName="offers"
                      data={props.aggregatedOfferData}
                      handleTableChange={handleAggregatedTableChange}
                      loader={props.loaderAggregatedOffer}
                      setTablesDataForExport={handleUpdateTable}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </StyledContainer>
        ) : (
          ""
        )}
      </React.Fragment>
    </>
  );
};

const StyledContainer = styled(Container)`
    .loader-for--line-chart {
      transition: opacity 0.3s;
      opacity: 1;
      backdrop-filter: blur(4px);
      display: flex;
      width: 98%;
      height: 350px;
      position: absolute;
      z-index: 1;
    }
  `;

const StyledManager = styled.div`
    margin-right: 10px;
    width: 200px;
  `;

const StyledCard = styled(Card)`
    .loader-for-react-widget {
      position: absolute;
      left: 0;
      width: 100%;
      height: 100%;
      right: 0;
      bottom: 0;
      top: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(255, 255, 255, 0.3);
      z-index: 1;
    }
  `;

DefaultOverview.propTypes = {
  history: PropTypes.any,
  topReportsFilters: PropTypes.object,
  topReportsWidgets: PropTypes.array,
  totalProfit: PropTypes.array,
  profitByDays: PropTypes.array,
  customProfitByDayData: PropTypes.array,
  // getTotalProfit: PropTypes.func,
  getCustomProfitByDays: PropTypes.func,
  getDataByDays: PropTypes.func,
  setDataByDays: PropTypes.func,
  setTotalProfit: PropTypes.func,
  setCustomProfitByDays: PropTypes.func,
  getTotalPlan: PropTypes.func,
  totalPlan: PropTypes.array,
  filter_data: PropTypes.object,
  getTopManagementReportsFilters: PropTypes.func,
  getProfitPlansWidgetData: PropTypes.func,
  topReportsLoading: PropTypes.bool,
  topReportsFiltersLoading: PropTypes.bool,
  setTopManagementReportsFilters: PropTypes.func,
  profitPlanOffers: PropTypes.object,
  getProfitPlanOffers: PropTypes.func,
  profitPlanMargin: PropTypes.any,
  profitPlanExecution: PropTypes.any,
  getExecPlan: PropTypes.func,
  getMarginPlan: PropTypes.func,
  customFilters: PropTypes.object,
  customLoader: PropTypes.bool,
  getCustomFilters: PropTypes.func,
  marginFilters: PropTypes.array,
  marginLoader: PropTypes.bool,
  getMarginFilter: PropTypes.func,
  aggregatedManagersData: PropTypes.object,
  loaderAggregatedManagers: PropTypes.bool,
  aggregatedOfferData: PropTypes.object,
  loaderAggregatedOffer: PropTypes.bool,
  loaderDataByDays: PropTypes.bool,
  dataByDays: PropTypes.any,
  getAggregatedManagersProfit: PropTypes.func,
  getAggregatedOfferProfit: PropTypes.func,
  user: PropTypes.object,
  permissions: PropTypes.any,
  setProfitPlansWidgetData: PropTypes.func,
};

export default DefaultOverview;
